import React, { useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronDown } from 'lucide-react';
import type { MapData } from '@/services/types';
import { cn } from '@/lib/utils';
import { usePostHog } from 'posthog-js/react';

interface SidebarFilterProps {
  data: MapData[];
  filterCategory: {id: string, name: string} | 'organisations'; // category ID for categories filter, "organisations" for organisation filter
  onFilterChange: (filter: Set<string>) => void;
  uncheckedItems: Set<string>;
}

const INITIAL_ITEMS_TO_SHOW = 5;

const SidebarFilter = ({
  data,
  filterCategory,
  onFilterChange,
  uncheckedItems
}: SidebarFilterProps) => {
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const posthog = usePostHog();

  const isAllChecked = uncheckedItems.size === 0;

  // Extract filter options based on filter type
  const filterOptions = useMemo(() => {
    if (!data?.length) return [];
    
    if (filterCategory === 'organisations') {
      // Extract unique organisations
      const organisationSet = new Set<string>();
      data.forEach((item) => {
        const orgName = typeof item.organisation === 'string'
          ? item.organisation
          : item.organisation?.name;
        organisationSet.add(orgName);
      });
      return Array.from(organisationSet).sort();
    } else {
      // Extract unique tags for the specific category
      const tagSet = new Set<string>();
      data.forEach((item) => {
        if (item.category === filterCategory.id) {
          item.tags.forEach(tag => tagSet.add(tag));
        }
      });
      return Array.from(tagSet).sort();
    }
  }, [data, filterCategory]);

  const filteredOptions = useMemo(() => {
    return filterOptions.filter(option =>
      option && option.toLowerCase().includes(search.toLowerCase())
    );
  }, [filterOptions, search]);

  // Reset showAll when search changes
  React.useEffect(() => {
    setShowAll(false);
  }, [search]);

  // Determine which options to display
  const displayOptions = useMemo(() => {
    if (showAll || filteredOptions.length <= INITIAL_ITEMS_TO_SHOW) {
      return filteredOptions;
    }
    return filteredOptions.slice(0, INITIAL_ITEMS_TO_SHOW);
  }, [filteredOptions, showAll]);

  const hasMoreItems = filteredOptions.length > INITIAL_ITEMS_TO_SHOW;

  if (!data?.length || !filterOptions.length) return null;

  const filterLabel = filterCategory === 'organisations' ? 'Organisations' : filterCategory.name;

  return (
    <div className='space-y-3'>
      <div>
        <span className='text-base font-bold text-gray-900 mb-2 block'>
          {filterLabel}
        </span>
        <Input
          className='mt-2'
          placeholder={`Search ${filterLabel.toLowerCase()}`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      
      <div className='space-y-1'>
        {/* All checkbox */}
        <div
          className='flex items-center space-x-2 p-1 cursor-pointer hover:bg-gray-50 rounded'
          onClick={() => {
            if (isAllChecked) {
              posthog?.capture('filter_applied', { filter_type: 'map_sidebar', label: filterLabel, value: 'all_deselected' });
              onFilterChange(new Set(filterOptions));
            } else {
              posthog?.capture('filter_applied', { filter_type: 'map_sidebar', label: filterLabel, value: 'all_selected' });
              onFilterChange(new Set());
            }
          }}
        >
          <Checkbox id={`All-${filterLabel}`} checked={isAllChecked} />
          <label
            htmlFor={`All-${filterLabel}`}
            className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer'
          >
            All
          </label>
        </div>
        
        {/* Filter items */}
        {displayOptions.map((option: string, index: number) => (
          <div
            className='flex items-center space-x-2 p-1 cursor-pointer hover:bg-gray-50 rounded'
            key={`${option}-${index}`}
            onClick={() => {
              if (uncheckedItems.has(option)) {
                const newSet = new Set([...uncheckedItems]);
                newSet.delete(option);
                posthog?.capture('filter_applied', { filter_type: 'map_sidebar', label: filterLabel, value: option, checked: true });
                onFilterChange(newSet);
              } else {
                const newSet = new Set([...uncheckedItems]);
                newSet.add(option);
                posthog?.capture('filter_applied', { filter_type: 'map_sidebar', label: filterLabel, value: option, checked: false });
                onFilterChange(newSet);
              }
            }}
          >
            <Checkbox
              id={`option-${option}-${index}`}
              checked={!uncheckedItems.has(option)}
            />
            <label
              htmlFor={`option-${option}-${index}`}
              className='text-sm font-medium capitalize leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer'
            >
              {option}
            </label>
          </div>
        ))}
        
        {/* Show All / Show Less link */}
        {hasMoreItems && !search && (
          <div
            className='flex items-center space-x-1 text-blue-600 hover:text-blue-700 cursor-pointer py-1 text-sm font-medium'
            onClick={() => setShowAll(!showAll)}
          >
            <span>{showAll ? 'Show Less' : `Show All ${filterLabel}`}</span>
            <ChevronDown
              className={cn(
                'h-4 w-4 transition-transform duration-200',
                showAll && 'rotate-180'
              )}
            />
          </div>
        )}
        
        {filteredOptions.length === 0 && search && (
          <div className='px-3 py-2 text-sm text-gray-500 text-center'>
            No results found for "{search}"
          </div>
        )}
      </div>
    </div>
  );
};

export default SidebarFilter;
